import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import { Form } from "react-bootstrap";
import { NavLink } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Select from "react-select";
import csvDownload from "json-to-csv-export";
import * as XLSX from "xlsx";
import config from "../../Config";
import PayrollMenu from "./PayrollMenu";
import EmployeeDropDownSearch from "../Shared/EmployeeDropDownSearch";

function normalizeSalarySheetRowKeys(row) {
  const out = {};
  if (!row || typeof row !== "object") return out;
  Object.keys(row).forEach((k) => {
    const nk = String(k).trim();
    if (nk) out[nk] = row[k];
  });
  return out;
}

function parseSalarySheetFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
        const normalized = rows
          .map(normalizeSalarySheetRowKeys)
          .filter((r) => Object.keys(r).length > 0);
        resolve(normalized);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
}

const GenerateEmployeesSalary = () => {
  const [accessKey] = useState(localStorage.getItem("AccessKey"));
  const [userID] = useState(localStorage.getItem("ID"));
  const [memberType] = useState(localStorage.getItem("MemberType"));

  const [mode, setMode] = useState("update");
  const [loading, setLoading] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [uploadingSheet, setUploadingSheet] = useState(false);
  const sheetFileInputRef = useRef(null);
  const [validated, setValidated] = useState(false);
  const allEmployeesOption = useMemo(
    () => ({ value: 0, label: "All Employees" }),
    []
  );
  const [selectedEmployee, setSelectedEmployee] = useState(allEmployeesOption);
  const employeeOptionsRaw = EmployeeDropDownSearch();
  const employeeOptions = useMemo(() => {
    const mapped = Array.isArray(employeeOptionsRaw) ? employeeOptionsRaw : [];
    return [allEmployeesOption, ...mapped];
  }, [employeeOptionsRaw, allEmployeesOption]);

  const submitHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      setValidated(true);
      return;
    }

    setLoading(true);
    const data = {
      AccessKey: accessKey,
      UserID: userID,
      MemberType: memberType,
      ID: selectedEmployee?.value ? Number(selectedEmployee.value) : 0,
      ReCreate: mode === "replace",
    };

    axios({
      method: "post",
      url: config.base_url + "Payroll/GenerateEmployeeSalaryData",
      headers: { "Content-Type": "application/json" },
      data: data,
    })
      .then((response) => {
        if (response.data.status_code === 1) {
          toast.success(
            `${response.data.status_message} Employees: ${response.data.processedEmployees}, Inserted: ${response.data.insertedRecords}, Updated: ${response.data.updatedRecords}`,
            { position: toast.POSITION.TOP_RIGHT }
          );
        } else {
          toast.error(response.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(() => {
        toast.error("Network Error..", { position: toast.POSITION.TOP_RIGHT });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const downloadSalaryDetails = () => {
    setLoadingDownload(true);
    const data = {
      AccessKey: accessKey,
      UserID: userID,
      MemberType: memberType,
      ID: selectedEmployee?.value ? Number(selectedEmployee.value) : 0,
    };

    axios({
      method: "post",
      url: config.base_url + "Payroll/GetGeneratedSalaryDetails",
      headers: { "Content-Type": "application/json" },
      data: data,
    })
      .then((response) => {
        if (response.data?.status_code !== 1 || !Array.isArray(response.data?.ListofRecords)) {
          toast.error(response.data?.status_message || "Unable to fetch salary details.", {
            position: toast.POSITION.TOP_RIGHT,
          });
          return;
        }

        const fileNamePart = selectedEmployee?.value ? `employee-${selectedEmployee.value}` : "all-employees";
        const dataToConvert = {
          data: response.data.ListofRecords,
          filename: `salary-details-${fileNamePart}`,
          delimiter: ",",
          headers: Object.keys(response.data.ListofRecords[0] || {}),
        };
        csvDownload(dataToConvert);
      })
      .catch(() => {
        toast.error("Network Error..", { position: toast.POSITION.TOP_RIGHT });
      })
      .finally(() => {
        setLoadingDownload(false);
      });
  };

  const uploadSalarySheet = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setUploadingSheet(true);
    try {
      const rows = await parseSalarySheetFile(file);
      if (!rows.length) {
        toast.error("The file has no data rows.", { position: toast.POSITION.TOP_RIGHT });
        return;
      }

      const res = await axios({
        method: "post",
        url: config.base_url + "Payroll/ImportSalarySheetDetails",
        headers: { "Content-Type": "application/json" },
        data: {
          AccessKey: accessKey,
          UserID: userID,
          MemberType: memberType,
          SalarySheetRows: rows,
        },
      });

      if (res.data?.status_code === 1) {
        const msg =
          `${res.data.status_message} Rows applied: ${res.data.rowsProcessed ?? 0}, ` +
          `salary cells updated: ${res.data.salaryCellsUpdated ?? 0}.`;
        toast.success(msg, { position: toast.POSITION.TOP_RIGHT });
        const warn = res.data.messages;
        if (Array.isArray(warn) && warn.length > 0) {
          toast.warning(warn.slice(0, 12).join(" "), {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 12000,
          });
        }
      } else {
        toast.error(res.data?.status_message || "Import failed.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch (e) {
      toast.error(e?.message || "Could not read or upload the file.", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setUploadingSheet(false);
    }
  };

  return (
    <>
      <PayrollMenu />
      <ToastContainer />
      <div className="page-head">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <NavLink to="/Dashboard">Dashboard</NavLink>
                  </li>
                  <li className="breadcrumb-item">
                    <NavLink to="/PayRoll/PayRollDashboard">PayRoll</NavLink>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Generate Employees Salary
                  </li>
                </ol>
              </nav>
              <div className="page-title">Generate Employees Salary</div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="card">
            <div className="card-body card-form-wrap">
              <div className="form-wrap">
                <div className="form-legend">Salary Generation Options</div>
                <Form noValidate validated={validated} onSubmit={submitHandler}>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Employee</label>
                        <Select
                          value={selectedEmployee}
                          options={employeeOptions}
                          onChange={(option) => setSelectedEmployee(option)}
                          isSearchable={true}
                          placeholder="Search employee..."
                        />
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Save Mode</label>
                        <select
                          className="form-select"
                          value={mode}
                          onChange={(e) => setMode(e.target.value)}
                        >
                          <option value="update">
                            Update existing and add missing rows
                          </option>
                          <option value="replace">
                            Delete existing system-generated rows and recreate
                          </option>
                        </select>
                      </div>
                    </div>

                    <div className="col-md-4"></div>
                  </div>

                  <div className="row mt-2">
                    <div className="col-md-12 text-end d-flex justify-content-end gap-2 flex-wrap">
                      <input
                        ref={sheetFileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        className="d-none"
                        onChange={uploadSalarySheet}
                      />
                      {loadingDownload ? (
                        <Spinner animation="border" role="status"></Spinner>
                      ) : (
                        <button
                          type="button"
                          className="button button-white"
                          onClick={downloadSalaryDetails}
                        >
                          Download Salary Details
                        </button>
                      )}
                      {uploadingSheet ? (
                        <Spinner animation="border" role="status"></Spinner>
                      ) : (
                        <button
                          type="button"
                          className="button button-white"
                          onClick={() => sheetFileInputRef.current?.click()}
                        >
                          Upload Salary Sheet
                        </button>
                      )}
                      {loading ? (
                        <Spinner animation="border" role="status"></Spinner>
                      ) : (
                        <input
                          type="submit"
                          value="Generate Employees Salary"
                          className="button button-primary"
                        />
                      )}
                    </div>
                  </div>
                </Form>
              </div>

              <div className="form-wrap">
                <p className="m-0">
                  <b>Calculation source:</b> Category + Pay Scale from{" "}
                  <code>tblPayRate</code>, increments from{" "}
                  <code>tblStaff_Master.NoofIncrements</code>, and output saved
                  into <code>tblEmployeeSalary</code>.
                </p>
                <p className="mt-2 mb-0">
                  <b>Bulk edit:</b> Download salary details (CSV or open in Excel),
                  edit salary columns only, then upload the saved CSV or Excel file.
                  Keep column headers unchanged; <b>Staff ID</b> with{" "}
                  <b>Employee Code</b> must match. Totals (<code>PAY_BILL_*</code>)
                  are ignored on import—only individual salary-type columns are saved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GenerateEmployeesSalary;
